import { useEffect } from "react";
import { useLocation } from "wouter";
import { roundIdFromPathname } from "../pages/round/funder-workspace/queue/roundIdFromPathname";
import { isIPadCapacitor } from "./isIPad";

// iPad-shell "reopen where I was": a cold start of the Capacitor WebView always
// loads at "/", so without this the app lands on the rounds list every time iOS
// evicts it. We persist the last round the user was in and, before React
// mounts, rewrite "/" to that round's base path. The round base path
// (`/round/:roundId`) is the canonical role-aware entry — `BasePathRedirector`
// sends funders to the Review tab of the funder workspace.

const STORAGE_KEY = "sProcessLastVisitedRound.v1";

// sessionStorage marker distinguishing a true cold start from an in-app
// full-document navigation. Set on every document load in the iPad shell;
// sessionStorage survives same-tab document loads (the round error page's
// raw `<a href="/">` recovery link, NotFoundCard's anchors, sign-out's
// `window.location.href = "/"`) but is recreated with the WebView process on
// a real cold start — the same discriminator the magic-link launch-URL
// consumed flag relies on (useMobileAuthDeepLink). Without it, a saved round
// that no longer loads (deleted round, access revoked) would hijack its own
// error page's "Go to homepage" link and loop back into the broken round.
const BOOTED_THIS_SESSION_KEY = "sProcessShellBootedThisSession";

// URL-safe id segment (nanoid alphabet). Anything else means the stored value
// is corrupt or was extracted from a garbage URL — don't boot into it.
const ROUND_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

// Pure decision, split out for unit tests: given the WebView's launch pathname,
// the persisted round id, and whether this document load is a true cold start
// (first load of this WebView process), where (if anywhere) should boot land
// instead?
export function resolveBootRedirectPath(
  pathname: string,
  savedRoundId: string | null,
  isColdStart: boolean,
): string | null {
  if (!isColdStart) {
    // An in-app full-document navigation to "/" is a deliberate "take me
    // home" (error-recovery links, sign-out) — never override it.
    return null;
  }
  if (pathname !== "/") {
    // Launched at a real destination (e.g. a deep link) — never override it.
    return null;
  }
  if (!savedRoundId || !ROUND_ID_PATTERN.test(savedRoundId)) {
    return null;
  }
  return `/round/${savedRoundId}`;
}

// Reads-and-stamps the boot marker: true only on the first document load of
// this WebView process. If sessionStorage is unavailable the marker can't be
// trusted (or persisted for the next load), so fail toward "not a cold start":
// the feature degrades to booting at Home, instead of every "/" load looking
// cold — which would hijack error-recovery links with no way to ever land on
// Home and clear the saved round.
function consumeColdStartMarker(): boolean {
  try {
    const isColdStart =
      window.sessionStorage.getItem(BOOTED_THIS_SESSION_KEY) == null;
    window.sessionStorage.setItem(BOOTED_THIS_SESSION_KEY, "1");
    return isColdStart;
  } catch {
    return false;
  }
}

function readLastVisitedRoundId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeLastVisitedRoundId(roundId: string | null): void {
  try {
    if (roundId == null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, roundId);
    }
  } catch {
    // Storage unavailable — the feature degrades to booting at Home.
  }
}

/**
 * The saved round is user-scoped state on a device-scoped key, so every
 * identity transition must drop it or the next account boots into the
 * previous user's round. Both transitions live in `iphone/lib/auth.ts`:
 * `clearAuthToken` (every sign-out path, which ends in a full document load
 * at "/") and `saveAuthToken`'s different-bearer branch (a new account signed
 * in without going through sign-out). Harmless no-op on web, where nothing is
 * saved.
 */
export function clearLastVisitedRound(): void {
  writeLastVisitedRoundId(null);
}

/**
 * Called at App.tsx module scope, BEFORE React mounts. Rewriting history
 * pre-render means the router mounts directly at the round — the user never
 * sees a flash of the Home page. No-op outside the native iPad shell.
 */
export function applyLastVisitedRoundBootRedirect(): void {
  if (!isIPadCapacitor()) {
    return;
  }
  const target = resolveBootRedirectPath(
    window.location.pathname,
    readLastVisitedRoundId(),
    consumeColdStartMarker(),
  );
  if (target != null) {
    window.history.replaceState(window.history.state, "", target);
  }
}

/**
 * Tracks the round the user is currently in (native iPad shell only).
 * Navigating to Home clears the memory: cold boots never render "/" when a
 * saved round exists (the pre-mount redirect rewrites it), so seeing "/" here
 * means the user deliberately went to the rounds list — closing the app there
 * should reopen there.
 */
export function useTrackLastVisitedRound(): void {
  const [location] = useLocation();

  useEffect(() => {
    if (!isIPadCapacitor()) {
      return;
    }
    const roundId = roundIdFromPathname(location);
    if (roundId != null) {
      writeLastVisitedRoundId(roundId);
    } else if (location === "/") {
      writeLastVisitedRoundId(null);
    }
  }, [location]);
}

/**
 * Null-rendering mount point for {@link useTrackLastVisitedRound}. The hook
 * subscribes to the router, so calling it directly in the App root re-renders
 * the entire tree on every navigation (measured as the bulk of the funder
 * workspace's tab-switch cost) — from this leaf, a location change re-renders
 * nothing but the leaf itself.
 */
export function TrackLastVisitedRound(): null {
  useTrackLastVisitedRound();
  return null;
}
